import { CloudUpload, X } from "lucide-react";
import { useCallback } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { Button } from "@/components/button";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

import type { SessionFileRow } from "../types/sessionFile";

import { renameAttachment } from "../api/confidence-checker.api";
import { FileRow } from "../components/FileRow";
import { SessionFileNameCombobox } from "../components/SessionFileNameCombobox";
import { UploadDropZone } from "../components/UploadDropZone";
import { useDcc } from "../contexts/DesignConfidenceCheckerProvider";
import { useDeleteConfirmations } from "../hooks/useDeleteConfirmations";
import {
  isSessionFileAttached,
  isSessionFileTransferring,
} from "../utils/reconcileInFlightUploads";

const RENAME_DEBOUNCE_MS = 500;

interface UploadStepProps {
  onCheckFiles: () => void;
  onClose: () => void;
  onSkipToSourceFiles?: () => void;
}

export function UploadStep({
  onCheckFiles,
  onClose,
  onSkipToSourceFiles,
}: UploadStepProps) {
  const { requestId, session } = useDcc();
  const { sessionFiles } = session;
  const onAdd = session.addFiles;
  const onCancelUpload = session.cancelUpload;
  const onDeleteAll = () => {
    void session.discardAndClose(() => {});
  };
  const onRemove = session.removeFile;
  const onUpdateFileMetadata = session.updateFileMetadata;

  const { deleteConfirmationsModals, requestDelete, requestDeleteAll } =
    useDeleteConfirmations<SessionFileRow>({
      deleteAllText: "All uploaded files will be removed from the session.",
      deleteAllTitle: "Delete all files?",
      deleteOneText: "This file will not be sent to the request.",
      deleteOneTitle: "Delete file?",
      onDeleteAll,
      onDeleteOne: onRemove,
    });

  const attachedCount = sessionFiles.filter((file) =>
    isSessionFileAttached(file),
  ).length;
  const isAnyTransferring = sessionFiles.some((file) =>
    isSessionFileTransferring(file),
  );
  const hasBlankName = sessionFiles.some(
    (file) => file.displayBaseName.trim() === "",
  );

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between border-b border-border-secondary px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border-secondary bg-bg-primary shadow-sm">
            <CloudUpload className="text-text-secondary" size={20} />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-semibold text-text-primary">
              Design confidence checker
            </span>
            <span className="text-sm text-text-tertiary">
              Evaluate your files before delivering to the customer. Upload only
              JPEG or PNG images for evaluation.
            </span>
          </div>
        </div>
        <button
          aria-label="Close"
          className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-bg-secondary"
          onClick={onClose}
          type="button"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex gap-4 p-6 flex-1 overflow-hidden">
        <UploadDropZone
          accept="image/jpeg,image/png"
          filterFiles={(files) =>
            files.filter(
              (file) => file.type === "image/jpeg" || file.type === "image/png",
            )
          }
          helperLine="Upload your design files for client review and approval."
          icon={<CloudUpload className="text-text-secondary" size={20} />}
          onFilesSelected={onAdd}
          warningLine="Do not upload source files here."
        />

        <div className="flex flex-1 flex-col gap-2 overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-text-primary">
              List of uploaded files
            </span>
            {sessionFiles.length > 0 && (
              <Button
                color="gray"
                isDisabled={isAnyTransferring}
                onPress={requestDeleteAll}
                size="sm"
                variant="tertiary"
              >
                Delete all
              </Button>
            )}
          </div>

          {sessionFiles.length === 0 ? (
            <p className="text-sm text-text-tertiary">No files uploaded yet</p>
          ) : (
            <div className="flex flex-col gap-3 overflow-y-auto pr-1">
              {sessionFiles.map((file) => (
                <SessionUploadFileRow
                  file={file}
                  key={file.clientId}
                  onCancelUpload={() => onCancelUpload(file.clientId)}
                  onRemove={() => {
                    if (file.status === "error") {
                      onRemove(file);

                      return;
                    }
                    requestDelete(file);
                  }}
                  onUpdateFileMetadata={onUpdateFileMetadata}
                  requestId={requestId}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border-secondary px-6 py-4">
        <div>
          {onSkipToSourceFiles && (
            <Button
              color="gray"
              isDisabled={sessionFiles.length > 0}
              onPress={onSkipToSourceFiles}
              size="lg"
              variant="tertiary"
            >
              Skip to source files uploads
            </Button>
          )}
        </div>
        <div className="flex gap-3">
          <Button
            color="color"
            isDisabled={attachedCount === 0 || hasBlankName}
            onPress={onCheckFiles}
            size="lg"
            variant="primary"
          >
            Continue
          </Button>
        </div>
      </div>

      {deleteConfirmationsModals}
    </div>
  );
}

function SessionUploadFileRow({
  file,
  onCancelUpload,
  onRemove,
  onUpdateFileMetadata,
  requestId,
}: {
  file: SessionFileRow;
  onCancelUpload: () => void;
  onRemove: () => void;
  onUpdateFileMetadata: ReturnType<
    typeof useDcc
  >["session"]["updateFileMetadata"];
  requestId: number;
}) {
  const isTransferring = isSessionFileTransferring(file);

  const extension =
    getExtensionFromFilename(file.displayName).toUpperCase() || "FILE";

  const persistRename = useCallback((attachmentId: number, name: string) => {
    void renameAttachment(attachmentId, name);
  }, []);
  const debouncedRename = useDebounceCallback(
    persistRename,
    RENAME_DEBOUNCE_MS,
  );

  const handleNameChange = useCallback(
    (
      value: { displayBaseName: string; previousVersionId: null | number },
      options?: { immediate?: boolean },
    ) => {
      onUpdateFileMetadata(file.clientId, value);

      if (file.attachmentId === null) return;

      if (value.displayBaseName.trim() === "") {
        debouncedRename.cancel();

        return;
      }

      const ext = getExtensionFromFilename(file.displayName);
      const fullName =
        ext === "unknown"
          ? value.displayBaseName
          : `${value.displayBaseName}.${ext}`;

      if (options?.immediate) {
        debouncedRename.cancel();
        persistRename(file.attachmentId, fullName);

        return;
      }

      debouncedRename(file.attachmentId, fullName);
    },
    [
      debouncedRename,
      persistRename,
      file.attachmentId,
      file.clientId,
      file.displayName,
      onUpdateFileMetadata,
    ],
  );

  return (
    <FileRow
      canCancel={file.status === "uploading"}
      canRemove={
        isSessionFileAttached(file) ||
        file.status === "error" ||
        file.status === "attaching"
      }
      combobox={
        <SessionFileNameCombobox
          displayBaseName={file.displayBaseName}
          isDisabled={isTransferring}
          onChange={handleNameChange}
          originalFileName={file.displayName}
          previousVersionId={file.previousVersionId}
          requestId={requestId}
        />
      }
      extension={extension}
      mime={file.mime}
      onCancelUpload={onCancelUpload}
      onRemove={onRemove}
      progress={file.progress}
      removeDisabled={
        file.status === "attaching" || file.status === "replacing"
      }
      showProgress={
        (file.status === "uploading" || file.status === "replacing") &&
        file.progress !== undefined
      }
      statusLabel={<SessionUploadStatusLabel file={file} />}
    />
  );
}

function SessionUploadStatusLabel({ file }: { file: SessionFileRow }) {
  switch (file.status) {
    case "uploading": {
      return (
        <span className="text-xs text-text-tertiary">
          {file.progress === undefined
            ? "Uploading…"
            : `Uploading ${file.progress}%`}
        </span>
      );
    }
    case "replacing": {
      return (
        <span className="text-xs text-text-tertiary">
          {file.progress === undefined
            ? "Replacing file…"
            : `Replacing file ${file.progress}%`}
        </span>
      );
    }
    case "attaching": {
      return (
        <span className="text-xs text-text-tertiary">Linking to session…</span>
      );
    }
    case "evaluating": {
      return <span className="text-xs text-text-tertiary">Evaluating…</span>;
    }
    case "ready": {
      return (
        <span className="text-xs font-medium text-text-success-primary">
          Ready
        </span>
      );
    }
    case "error": {
      return (
        <span className="truncate text-xs text-text-error-primary">
          {file.error ?? "Upload failed"}
        </span>
      );
    }
    default: {
      return null;
    }
  }
}
